/* Modal editor de zona.
La ubicación tiene un layout, este modal permite modificar la estructura dentro de esa zona
Agregar mesas, asientos, cuántos, dónde, etc */
import React, { useState, useRef, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, TextField, FormControl, Select, MenuItem, Paper, Tooltip, Chip, IconButton } from '@mui/material';
import IconSelector from "../../ubicaciones/IconSelector";
import { api } from '../../../services/api';
import { Stage, Layer, Rect, Circle, Text, Transformer, Group } from 'react-konva';
import { DeleteOutline, InfoOutlined, RotateLeft, RotateRight } from '@mui/icons-material';
import { toast } from "react-toastify";

const EditorLayout = ({ open, onClose, zona, onSave }) => {
  const [elementos, setElementos] = useState([]);
  const [modoCreacion, setModoCreacion] = useState(null);
  const [elementoSeleccionado, setElementoSeleccionado] = useState(null);
  const [configuracion, setConfiguracion] = useState({
    mesasConfig: {
      asientosPorMesa: 5,
      numeradas: true,
      icono: 'circulo'
    },
    filasConfig: {
      asientosPorFila: 10,
      numeroFilas: 3,
      espaciado: 40,
      icono: 'circulo'
    }
  });
  const [loading, setLoading] = useState(false);

  const stageRef = useRef();
  const transformerRef = useRef();
  const CANVAS_WIDTH = 700;
  const CANVAS_HEIGHT = 600;

  const [filaSeleccionada, setFilaSeleccionada] = useState(null);
  const [tipoSeleccion, setTipoSeleccion] = useState(null); // fila o elemento
  const [transformerVisible, setTransformerVisible] = useState(false);

  const obtenerElementosDeFila = (nombreFila) => {
    return elementos.filter(elemento =>
      elemento.tipo === 'asiento' && elemento.grupoFila === nombreFila
    );
  };

  const getNumeroAsiento = (asiento) => {
  let label = asiento.numero || asiento.asiento || asiento.id;
  if (typeof label === 'string') {
    const match = label.match(/(\d+)/);
    if (match) {
      label = match[1];
    }
  }
  return label;
};

  const calcularBoundingBox = () => {
    if (!elementos || elementos.length === 0) {
      return {
        minX: 0,
        minY: 0,
        maxX: CANVAS_WIDTH,
        maxY: CANVAS_HEIGHT,
        centerX: CANVAS_WIDTH / 2,
        centerY: CANVAS_HEIGHT / 2
      };
    }

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    let hasElements = false;

    elementos.forEach(elemento => {
      if (elemento.x !== undefined && elemento.y !== undefined) {
        hasElements = true;

        if (elemento.tipo === 'mesa') {
          const mesaMinX = elemento.x;
          const mesaMaxX = elemento.x + (elemento.width || 80);
          const mesaMinY = elemento.y;
          const mesaMaxY = elemento.y + (elemento.height || 80);

          minX = Math.min(minX, mesaMinX);
          maxX = Math.max(maxX, mesaMaxX);
          minY = Math.min(minY, mesaMinY);
          maxY = Math.max(maxY, mesaMaxY);
        } else if (elemento.tipo === 'asiento') {
          const asientoSize = (elemento.radius || 20) * 2;
          const asientoMinX = elemento.x - asientoSize / 2;
          const asientoMaxX = elemento.x + asientoSize / 2;
          const asientoMinY = elemento.y - asientoSize / 2;
          const asientoMaxY = elemento.y + asientoSize / 2;

          minX = Math.min(minX, asientoMinX);
          maxX = Math.max(maxX, asientoMaxX);
          minY = Math.min(minY, asientoMinY);
          maxY = Math.max(maxY, asientoMaxY);
        }
      }
    });

    if (!hasElements) {
      return {
        minX: 0,
        minY: 0,
        maxX: CANVAS_WIDTH,
        maxY: CANVAS_HEIGHT,
        centerX: CANVAS_WIDTH / 2,
        centerY: CANVAS_HEIGHT / 2
      };
    }

    const padding = 50;
    minX -= padding;
    minY -= padding;
    maxX += padding;
    maxY += padding;

    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;

    return { minX, minY, maxX, maxY, centerX, centerY };
  };

  const centrarCanvas = () => {
    if (!elementos || elementos.length === 0) {
      setEscala(1);
      setPosicion({ x: 0, y: 0 });
      return;
    }

    const boundingBox = calcularBoundingBox();
    const { minX, minY, maxX, maxY, centerX, centerY } = boundingBox;

    const contentWidth = maxX - minX;
    const contentHeight = maxY - minY;

    const scaleX = CANVAS_WIDTH / contentWidth;
    const scaleY = CANVAS_HEIGHT / contentHeight;
    const optimalScale = Math.min(scaleX, scaleY, 1.5);

    const scaledCenterX = centerX * optimalScale;
    const scaledCenterY = centerY * optimalScale;

    const offsetX = (CANVAS_WIDTH / 2) - scaledCenterX;
    const offsetY = (CANVAS_HEIGHT / 2) - scaledCenterY;

    setEscala(optimalScale);
    setPosicion({ x: offsetX, y: offsetY });
  };

  const handleClose = (event, reason) => {
    if (reason === "backdropClick" || reason === "escapeKeyDown") return;
    setModoCreacion(null);
    onClose();
  };

  useEffect(() => {
    if (!open) {
      setModoCreacion(null);
      setElementoSeleccionado(null);
      setElementos([]);
    }
  }, [open]);

  useEffect(() => {
    if (elementos.length > 0) {
      setTimeout(() => {
        centrarCanvas();
      }, 100);
    }
  }, [elementos.length]);

  useEffect(() => {
    if (open && zona) {
      cargarLayoutExistente();
    }
  }, [open, zona]);

  const rotarFilaSeleccionada = (angulo) => {
    if (!filaSeleccionada) return;
    const elementosFila = obtenerElementosDeFila(filaSeleccionada);
    if (elementosFila.length === 0) return;

    // centro de la fila
    const centerX = elementosFila.reduce((sum, el) => sum + el.x, 0) / elementosFila.length;
    const centerY = elementosFila.reduce((sum, el) => sum + el.y, 0) / elementosFila.length;

    const rad = (angulo * Math.PI) / 180;

    const nuevosElementos = elementosFila.map(el => {
      const relX = el.x - centerX;
      const relY = el.y - centerY;
      const rotX = relX * Math.cos(rad) - relY * Math.sin(rad);
      const rotY = relX * Math.sin(rad) + relY * Math.cos(rad);
      return {
        ...el,
        x: centerX + rotX,
        y: centerY + rotY,
        rotation: ((el.rotation || 0) + angulo) % 360
      };
    });

    setElementos(prev =>
      prev.map(e => {
        const nuevo = nuevosElementos.find(ne => ne.id === e.id);
        return nuevo || e;
      })
    );
  };

  const cargarLayoutExistente = async () => {
    setLoading(true);
    try {
      const response = await api.ubicacionesNew.getLayoutZona(zona.id);
      if (response.status === "success" && response.data.ubicacion) {
        const layout = response.data.ubicacion;
        const elementosExistentes = [];

        if (layout.mesas) {
          layout.mesas.forEach(mesa => {
            const esNumerada = mesa.asientos && mesa.asientos.some(asiento => asiento.visible_cliente === true);
            const centroX = mesa.coordX - (mesa.ancho / 2 || 0);
            const centroY = mesa.coordY - (mesa.alto / 2 || 0);

            elementosExistentes.push({
              id: `mesa_${mesa.id}`,
              tipo: 'mesa',
              x: centroX,
              y: centroY,
              rotation: mesa.rotation || 0,
              width: mesa.ancho,
              height: mesa.alto,
              asientos: mesa.capacidad_total,
              numerada: esNumerada,
              dbId: mesa.id,
              nombre: mesa.mesa,
              icono: mesa.icono || 'circulo'
            });
          });
        }

        if (layout.asientos) {
          layout.asientos
            .filter(asiento => !asiento.idmesa && asiento.visible_cliente === true)
            .forEach(asiento => {
              elementosExistentes.push({
                id: `asiento_${asiento.id}`,
                tipo: 'asiento',
                x: asiento.coordX,
                y: asiento.coordY,
                rotation: asiento.rotation || 0,
                radius: 15,
                fila: asiento.fila || '',
                numero: asiento.asiento,
                grupoFila: asiento.fila || null,
                dbId: asiento.id
              });
            });
        }

        setElementos(elementosExistentes);
      }
    } catch (error) {
      toast.error("Error cargando layout.", {
        position: "top-right",
        autoClose: 4000,
      });
      console.error("Error cargando layout:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (transformerRef.current) {
      transformerRef.current.nodes([]);
    }

    if (transformerVisible && transformerRef.current && stageRef.current) {
      const stage = stageRef.current;

      if (tipoSeleccion === 'fila' && filaSeleccionada) {
        actualizarTransformerFila();

      } else if (tipoSeleccion === 'elemento' && elementoSeleccionado) {
        const selectedNode = stage.findOne(`#${elementoSeleccionado.id}`);

        if (selectedNode) {
          transformerRef.current.nodes([selectedNode]);
          transformerRef.current.moveToTop();
          transformerRef.current.getLayer()?.batchDraw();
        }
      }
    }

    return () => {
      if (transformerRef.current && stageRef.current) {
        const stage = stageRef.current;
        const layer = stage.findOne('Layer');

        const tempRects = layer.find('Rect').filter(rect =>
          rect.stroke() === '#4CAF50' && rect.fill() === 'transparent'
        );

        tempRects.forEach(rect => {
          if (rect._cleanup) rect._cleanup();
          rect.destroy();
        });

        transformerRef.current.nodes([]);
      }
    };
  }, [transformerVisible, tipoSeleccion, filaSeleccionada, elementoSeleccionado]);

  useEffect(() => {
    if (elementoSeleccionado && !elementos.find(e => e.id === elementoSeleccionado.id)) {
      setElementoSeleccionado(null);
      setTipoSeleccion(null);
      setTransformerVisible(false);
      if (transformerRef.current) {
        transformerRef.current.nodes([]);
      }
    }

    if (filaSeleccionada && obtenerElementosDeFila(filaSeleccionada).length === 0) {
      setFilaSeleccionada(null);
      setTipoSeleccion(null);
      setTransformerVisible(false);
      if (transformerRef.current) {
        transformerRef.current.nodes([]);
      }
    }
  }, [elementos, elementoSeleccionado, filaSeleccionada]);

  const handleFilaTransformEnd = (e) => {
    if (tipoSeleccion !== 'fila' || !filaSeleccionada) return;

    const node = e.target;
    const elementosFila = obtenerElementosDeFila(filaSeleccionada);
    if (elementosFila.length === 0) return;

    const posicionesOriginales = elementosFila.map(el => ({ x: el.x, y: el.y, id: el.id }));

    const rotation = node.rotation();
    const scaleX = node.scaleX();
    const scaleY = node.scaleY();
    const nodeX = node.x();
    const nodeY = node.y();

    const centerX = posicionesOriginales.reduce((sum, el) => sum + el.x, 0) / posicionesOriginales.length;
    const centerY = posicionesOriginales.reduce((sum, el) => sum + el.y, 0) / posicionesOriginales.length;

    const nuevosElementos = posicionesOriginales.map((elemento, idx) => {
      const relativeX = elemento.x - centerX;
      const relativeY = elemento.y - centerY;

      const cos = Math.cos(rotation);
      const sin = Math.sin(rotation);

      const rotatedX = relativeX * cos - relativeY * sin;
      const rotatedY = relativeX * sin + relativeY * cos;

      const scaledX = rotatedX * scaleX;
      const scaledY = rotatedY * scaleY;

      return {
        ...elementosFila[idx],
        x: nodeX + scaledX,
        y: nodeY + scaledY,
        rotation: rotation
      };
    });

    setElementos(prevElementos =>
      prevElementos.map(elemento => {
        const nuevoElemento = nuevosElementos.find(ne => ne.id === elemento.id);
        return nuevoElemento || elemento;
      })
    );

    setFilaSeleccionada(null);
    setTipoSeleccion(null);
    setTransformerVisible(false);
    if (transformerRef.current) transformerRef.current.nodes([]);
  };

  const actualizarTransformerFila = () => {
    if (!transformerRef.current || !stageRef.current || !filaSeleccionada) return;

    const elementosFila = obtenerElementosDeFila(filaSeleccionada);
    if (elementosFila.length === 0) return;

    const stage = stageRef.current;
    const layer = stage.findOne('Layer');

    layer.find('Rect').filter(rect =>
      rect.stroke() === '#4CAF50' && rect.fill() === 'transparent'
    ).forEach(rect => rect.destroy());

    const minX = Math.min(...elementosFila.map(el => el.x - el.radius));
    const maxX = Math.max(...elementosFila.map(el => el.x + el.radius));
    const minY = Math.min(...elementosFila.map(el => el.y - el.radius));
    const maxY = Math.max(...elementosFila.map(el => el.y + el.radius));

    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;
    const width = maxX - minX + 20;
    const height = maxY - minY + 20;

    const tempRect = new window.Konva.Rect({
      x: centerX,
      y: centerY,
      width: width,
      height: height,
      offsetX: width / 2,
      offsetY: height / 2,
      fill: 'transparent',
      stroke: '#4CAF50',
      strokeWidth: 2,
      dash: [8, 4],
      listening: true,
      draggable: true
    });

    tempRect.on('dragend', (e) => {
      const node = e.target;
      const deltaX = node.x() - centerX;
      const deltaY = node.y() - centerY;

      setElementos(prevElementos =>
        prevElementos.map(elemento => {
          if (elemento.tipo === 'asiento' && elemento.grupoFila === filaSeleccionada) {
            return {
              ...elemento,
              x: elemento.x + deltaX,
              y: elemento.y + deltaY
            };
          }
          return elemento;
        })
      );

      node.x(centerX);
      node.y(centerY);

      setTimeout(() => {
        actualizarTransformerFila();
      }, 50);
    });

    layer.add(tempRect);
    transformerRef.current.nodes([tempRect]);
    transformerRef.current.moveToTop();
    layer.batchDraw();

    tempRect._cleanup = () => {
      tempRect.destroy();
      transformerRef.current.nodes([]);
    };
  };

  const handleCanvasClick = (e) => {
    if (modoCreacion) {
      crearElemento(e);
    } else {
      if (e.target === e.target.getStage()) {
        setElementoSeleccionado(null);
        setFilaSeleccionada(null);
        setTipoSeleccion(null);
        setTransformerVisible(false);

        if (transformerRef.current && stageRef.current) {
          const stage = stageRef.current;
          const layer = stage.findOne('Layer');

          const tempRects = layer.find('Rect').filter(rect =>
            rect.stroke() === '#4CAF50' && rect.fill() === 'transparent'
          );

          tempRects.forEach(rect => rect.destroy());

          transformerRef.current.nodes([]);
          layer.batchDraw();
        }
      }
    }
  };

  const getCanvasCoordinates = (pointer) => {
    if (!stageRef.current) return pointer;

    const stage = stageRef.current;
    return {
      x: (pointer.x - stage.x()) / stage.scaleX(),
      y: (pointer.y - stage.y()) / stage.scaleY()
    };
  };

  const crearElemento = (e) => {
    const stage = e.target.getStage();
    const pointer = stage.getPointerPosition();
    const canvasCoords = getCanvasCoordinates(pointer);

    if (modoCreacion === 'mesa') {
      const nuevaMesa = {
        id: `mesa_nuevo_${Date.now()}`,
        tipo: 'mesa',
        x: canvasCoords.x,
        y: canvasCoords.y,
        width: 80,
        height: 80,
        asientos: configuracion.mesasConfig.asientosPorMesa,
        numerada: configuracion.mesasConfig.numeradas,
        icono: configuracion.mesasConfig.icono,
        nombre: `Mesa ${elementos.filter(e => e.tipo === 'mesa').length + 1}`
      };
      setElementos([...elementos, nuevaMesa]);
      setTimeout(() => centrarCanvas(), 50);
    } else if (modoCreacion === 'asiento') {
      const siguienteNumero = obtenerSiguienteNumeroAsiento();
      const nuevoAsiento = {
        id: `asiento_nuevo_${Date.now()}`,
        tipo: 'asiento',
        x: canvasCoords.x,
        y: canvasCoords.y,
        radius: 20,
        icono: configuracion.filasConfig.icono,
        numero: siguienteNumero,
        fila: '',
        grupoFila: null
      };
      setElementos([...elementos, nuevoAsiento]);
      setTimeout(() => centrarCanvas(), 50);
    }
  };

  const crearFilas = () => {
    const { asientosPorFila, numeroFilas, espaciado } = configuracion.filasConfig;
    const nuevasFilas = [];
    let letraInicio = obtenerSiguienteLetraFila();

    for (let fila = 0; fila < numeroFilas; fila++) {
      const numeroLetra = letraInicio.charCodeAt(0) - 65 + fila;
      const nombreFila = String.fromCharCode(65 + (numeroLetra % 26));
      for (let asiento = 0; asiento < asientosPorFila; asiento++) {
        nuevasFilas.push({
          id: `fila_${nombreFila}_${asiento}_${Date.now()}_${fila}`,
          tipo: 'asiento',
          x: Math.max(50, 50 + (asiento * 35)),
          y: Math.max(50, 50 + (fila * espaciado)),
          radius: 20,
          fila: nombreFila,
          numero: asiento + 1,
          grupoFila: nombreFila,
          icono: configuracion.filasConfig.icono
        });
      }
    }
    setElementos([...elementos, ...nuevasFilas]);
    setModoCreacion(null);

    setTimeout(() => centrarCanvas(), 100);
  };

  const obtenerSiguienteLetraFila = () => {
    const filasExistentes = new Set();
    elementos.forEach(elemento => {
      if (elemento.tipo === 'asiento' && elemento.grupoFila) {
        filasExistentes.add(elemento.grupoFila);
      }
    });
    for (let i = 0; i < 26; i++) {
      const letra = String.fromCharCode(65 + i);
      if (!filasExistentes.has(letra)) {
        return letra;
      }
    }
    for (let i = 0; i < 26; i++) {
      for (let j = 0; j < 26; j++) {
        const letra = String.fromCharCode(65 + i) + String.fromCharCode(65 + j);
        if (!filasExistentes.has(letra)) {
          return letra;
        }
      }
    }
    return 'A';
  };

  const obtenerSiguienteNumeroAsiento = () => {
    const numerosUsados = new Set();
    elementos.forEach(elemento => {
      if (elemento.tipo === 'asiento' && !elemento.grupoFila && !elemento.fila) {
        const numero = parseInt(elemento.numero) || parseInt(elemento.id.split('_').pop());
        if (!isNaN(numero)) {
          numerosUsados.add(numero);
        }
      }
    });
    for (let i = 1; i <= 1000; i++) {
      if (!numerosUsados.has(i)) {
        return i;
      }
    }
    return 1;
  };

  const eliminarSeleccionado = () => {
    if (tipoSeleccion === 'fila' && filaSeleccionada) {
      // eliminamos la fila completa
      if (window.confirm(`¿Eliminar toda la fila ${filaSeleccionada}?`)) {
        setElementos(elementos.filter(e =>
          !(e.tipo === 'asiento' && e.grupoFila === filaSeleccionada)
        ));
        setFilaSeleccionada(null);
        setTipoSeleccion(null);
        setTransformerVisible(false);
      }
    } else if (tipoSeleccion === 'elemento' && elementoSeleccionado) {
      // eliminar solo un elemento
      const mensaje = elementoSeleccionado.grupoFila
        ? `¿Eliminar el asiento ${elementoSeleccionado.fila}${elementoSeleccionado.numero}?`
        : `¿Eliminar este elemento?`;

      if (window.confirm(mensaje)) {
        setElementos(elementos.filter(e => e.id !== elementoSeleccionado.id));
        setElementoSeleccionado(null);
        setTipoSeleccion(null);
        setTransformerVisible(false);
      }
    }
  };

  const handleTransform = (e) => {
    const node = e.target;
    if (!node || !node.getParent()) return;

    const scaleX = node.scaleX();
    const scaleY = node.scaleY();
    const rotation = node.rotation();

    const elementoId = node.getParent().id();
    const elemento = elementos.find(el => el.id === elementoId);

    if (elemento && elemento.tipo === 'mesa') {
      const newWidth = Math.max(40, Math.min(200, Math.round(elemento.width * scaleX)));
      const newHeight = Math.max(40, Math.min(200, Math.round(elemento.height * scaleY)));

      setElementos(prevElementos =>
        prevElementos.map(el =>
          el.id === elemento.id
            ? {
              ...el,
              width: newWidth,
              height: newHeight,
              rotation: rotation
            }
            : el
        )
      );

      node.scaleX(1);
      node.scaleY(1);
    }
  };

  const handleMesaTransformEnd = (e) => {
    const node = e.target;

    node.scaleX(1);
    node.scaleY(1);

    const finalX = node.x();
    const finalY = node.y();
    const finalRotation = node.rotation();

    const elementoId = node.getParent().id();
    const elemento = elementos.find(el => el.id === elementoId);

    if (elemento) {
      setElementos(prevElementos =>
        prevElementos.map(el =>
          el.id === elemento.id
            ? {
              ...el,
              x: finalX,
              y: finalY,
              rotation: finalRotation
            }
            : el
        )
      );
    }
  };

  const handleElementoTransformEnd = (e) => {
    const node = e.target;
    const elementoId = node.getParent().id();
    const elemento = elementos.find(el => el.id === elementoId);

    if (elemento) {
      setElementos(prevElementos =>
        prevElementos.map(el =>
          el.id === elemento.id
            ? {
              ...el,
              x: node.x(),
              y: node.y(),
              rotation: node.rotation(),
              radius: el.radius * node.scaleX()
            }
            : el
        )
      );
      node.scaleX(1);
      node.scaleY(1);
    }
  };

  const ElementoCanvas = ({ elemento }) => {
    const isElementoSelected = elementoSeleccionado?.id === elemento.id;
    const isFilaSelected = filaSeleccionada === elemento.grupoFila && elemento.grupoFila;
    const isSelected = isElementoSelected || isFilaSelected;

    const handleClick = (e) => {
      e.cancelBubble = true;
      e.evt.stopPropagation();

      if (transformerRef.current) {
        transformerRef.current.nodes([]);
      }

      if (elemento.tipo === 'asiento' && elemento.grupoFila) {
        setFilaSeleccionada(elemento.grupoFila);
        setElementoSeleccionado(null);
        setTipoSeleccion('fila');
        setTransformerVisible(true);
      } else {
        setElementoSeleccionado(elemento);
        setFilaSeleccionada(null);
        setTipoSeleccion('elemento');
        setTransformerVisible(true);
      }
    };

    const handleDoubleClick = (e) => {
      e.cancelBubble = true;
      e.evt.stopPropagation();

      setElementoSeleccionado(elemento);
      setFilaSeleccionada(null);
      setTipoSeleccion('elemento');
      setTransformerVisible(true);

      if (transformerRef.current) {
        transformerRef.current.nodes([e.target.getParent()]);
        transformerRef.current.moveToTop();
      }
    };

    const handleDragEnd = (e) => {
      e.cancelBubble = true;
      e.evt.stopPropagation();

      if (tipoSeleccion === 'elemento' && elementoSeleccionado?.id === elemento.id) {
        const node = e.target;
        setElementos(elementos.map(el =>
          el.id === elemento.id ? {
            ...el,
            x: node.x(),
            y: node.y(),
            rotation: node.rotation()
          } : el
        ));
      }
    };

    const renderMesa = (elemento) => {
      const centroX = elemento.x + elemento.width / 2;
      const centroY = elemento.y + elemento.height / 2;
      const radioAsientos = Math.min(elemento.width, elemento.height) / 2 + Math.max(15, elemento.width * 0.25);

      const asientos = [];
      if (elemento.numerada === true) {
        const radioAsiento = Math.max(8, Math.min(15, elemento.width * 0.15));

        for (let i = 0; i < elemento.asientos; i++) {
          const angulo = (2 * Math.PI * i) / elemento.asientos;
          const asientoX = centroX + radioAsientos * Math.cos(angulo);
          const asientoY = centroY + radioAsientos * Math.sin(angulo);

          asientos.push(
            <Circle
              key={`asiento-${elemento.id}-${i}`}
              x={asientoX}
              y={asientoY}
              radius={radioAsiento}
              fill="#FFF"
              stroke="#1976D2"
              strokeWidth={elemento.width > 40 ? 2 : 1}
              listening={false}
              rotation={elemento.rotation || 0}
            />
          );

          asientos.push(
            <Text
              key={`asiento-txt-${elemento.id}-${i}`}
              x={asientoX}
              y={asientoY}
              text={`${i + 1}`}
              fontSize={Math.max(8, Math.min(14, elemento.width * 0.15))}
              fill="#1976D2"
              listening={false}
              offsetX={Math.max(4, Math.min(7, elemento.width * 0.075))}
              offsetY={Math.max(4, Math.min(7, elemento.width * 0.075))}
            />
          );
        }
      }

      const radius = Math.min(elemento.width, elemento.height) / 2;

      return (
        <Group
          id={elemento.id}
          x={elemento.x}
          y={elemento.y}
          draggable={tipoSeleccion === 'elemento' && isElementoSelected}
          onDragEnd={handleDragEnd}
          onClick={handleClick}
          onDblClick={handleDoubleClick}
        >
          <Circle
            x={elemento.width / 2}
            y={elemento.height / 2}
            radius={radius}
            fill={isSelected ? "#4CAF50" : "#8BC34A"}
            stroke={isSelected ? "#2E7D32" : "#689F38"}
            strokeWidth={isSelected ? 3 : 2}
            rotation={elemento.rotation || 0}
          />

          <Text
            x={elemento.width / 2}
            y={elemento.height / 2}
            text={elemento.numerada ?
              `Mesa ${elementos.filter(e => e.tipo === 'mesa').findIndex(m => m.id === elemento.id) + 1}\n${elemento.asientos} asientos` :
              `Mesa ${elementos.filter(e => e.tipo === 'mesa').findIndex(m => m.id === elemento.id) + 1}\n${elemento.asientos} personas`
            }
            fontSize={Math.max(10, Math.min(16, elemento.width * 0.2))}
            fill="white"
            listening={false}
            offsetX={Math.max(10, Math.min(16, elemento.width * 0.2)) * 2}
            offsetY={Math.max(10, Math.min(16, elemento.width * 0.2)) * 0.7}
            align="center"
          />

          {elemento.numerada && asientos.map((asiento, index) => {
            if (React.isValidElement(asiento)) {
              return React.cloneElement(asiento, {
                key: asiento.key,
                x: asiento.props.x - elemento.x,
                y: asiento.props.y - elemento.y
              });
            }
            return asiento;
          })}
        </Group>
      );
    };

    const renderAsiento = (elemento) => {
      const { icono = 'circulo' } = elemento;

      return (
        <Group
          id={elemento.id}
          x={elemento.x}
          y={elemento.y}
          draggable={tipoSeleccion === 'elemento' && isElementoSelected}
          onDragEnd={handleDragEnd}
          onClick={handleClick}
          onDblClick={handleDoubleClick}
        >
          {icono === 'silla' ? (
            <Group rotation={elemento.rotation || 0}>
              <Rect
                x={-8}
                y={-8}
                width={16}
                height={16}
                fill={isSelected ? "#1976D2" : "#2196F3"}
                stroke={isSelected ? "#0D47A1" : "#1976D2"}
                strokeWidth={isSelected ? 3 : 1}
              />
              <Rect
                x={-4}
                y={-12}
                width={12}
                height={4}
                fill={isSelected ? "#1976D2" : "#2196F3"}
                listening={false}
              />
            </Group>
          ) : (
            <Circle
              x={0}
              y={0}
              radius={elemento.radius}
              fill={isSelected ? "#1976D2" : "#2196F3"}
              stroke={isSelected ? "#0D47A1" : "#1976D2"}
              strokeWidth={isSelected ? 3 : 1}
              rotation={elemento.rotation || 0}
            />
          )}

          <Text
            x={0}
            y={0}
            text={getNumeroAsiento(elemento)}
            fontSize={Math.max(10, Math.min(14, elemento.radius * 0.6))}
            fill="white"
            listening={false}
            offsetX={Math.max(10, Math.min(14, elemento.radius * 0.6)) * 0.4}
            offsetY={Math.max(10, Math.min(14, elemento.radius * 0.6)) * 0.5}
          />
          {isFilaSelected && (
            <Group>
              <Circle
                x={0}
                y={0}
                radius={elemento.radius + 3}
                stroke="#4CAF50"
                strokeWidth={2}
                dash={[5, 5]}
                listening={false}
                rotation={elemento.rotation || 0}
              />
              <Circle
                x={0}
                y={0}
                radius={elemento.radius + 6}
                stroke="#4CAF50"
                strokeWidth={1}
                opacity={0.5}
                listening={false}
                rotation={elemento.rotation || 0}
              />
            </Group>
          )}
        </Group>
      );
    };

    if (elemento.tipo === 'mesa') {
      return renderMesa(elemento);
    } else if (elemento.tipo === 'asiento') {
      return renderAsiento(elemento);
    }
    return null;
  };

  const validarNoSolapamiento = (elementos) => {
    const mesas = elementos.filter(e => e.tipo === 'mesa');

    for (let i = 0; i < mesas.length; i++) {
      for (let j = i + 1; j < mesas.length; j++) {
        const mesa1 = mesas[i];
        const mesa2 = mesas[j];

        const nombreMesa1 = mesa1.nombre || `Mesa ${i + 1}`;
        const nombreMesa2 = mesa2.nombre || `Mesa ${j + 1}`;

        const margen = 5;

        if (!(mesa1.x + mesa1.width + margen < mesa2.x ||
          mesa1.x > mesa2.x + mesa2.width + margen ||
          mesa1.y + mesa1.height + margen < mesa2.y ||
          mesa1.y > mesa2.y + mesa2.height + margen)) {
          return `${nombreMesa1} y ${nombreMesa2} se solapan. Sepáralas al menos ${margen}px.`;
        }
      }
    }

    const asientosIndividuales = elementos.filter(e => e.tipo === 'asiento' && !e.grupoFila);
    for (let i = 0; i < asientosIndividuales.length; i++) {
      for (let j = i + 1; j < asientosIndividuales.length; j++) {
        const asiento1 = asientosIndividuales[i];
        const asiento2 = asientosIndividuales[j];

        const distancia = Math.sqrt(
          Math.pow(asiento1.x - asiento2.x, 2) +
          Math.pow(asiento1.y - asiento2.y, 2)
        );

        if (distancia < (asiento1.radius + asiento2.radius + 5)) {
          return `Los asientos ${asiento1.numero} y ${asiento2.numero} están demasiado cerca.`;
        }
      }
    }

    return null;
  };

  const ajustarCoordenadasPositivas = (elementos) => {
    let minX = Infinity;
    let minY = Infinity;

    elementos.forEach(e => {
      if (e.x !== undefined) minX = Math.min(minX, e.x);
      if (e.y !== undefined) minY = Math.min(minY, e.y);
    });

    const offsetX = minX < 0 ? Math.abs(minX) + 10 : 0;
    const offsetY = minY < 0 ? Math.abs(minY) + 10 : 0;

    if (offsetX !== 0 || offsetY !== 0) {
      return elementos.map(e => ({
        ...e,
        x: e.x !== undefined ? e.x + offsetX : e.x,
        y: e.y !== undefined ? e.y + offsetY : e.y
      }));
    }
    return elementos;
  };

  const guardarLayout = async () => {
    setLoading(true);
    try {
      const errorSolapamiento = validarNoSolapamiento(elementos);
      if (errorSolapamiento) {
        toast.error(errorSolapamiento, {
          position: "top-right",
          autoClose: 4000,
        });
        setLoading(false);
        return;
      }

      const elementosAjustados = ajustarCoordenadasPositivas(elementos);

      const asientosPorFila = elementosAjustados.filter(e => e.tipo === 'asiento' && e.grupoFila);
      const filasAgrupadas = {};
      asientosPorFila.forEach(asiento => {
        if (!filasAgrupadas[asiento.grupoFila]) {
          filasAgrupadas[asiento.grupoFila] = [];
        }
        filasAgrupadas[asiento.grupoFila].push(asiento);
      });

      const filasParaBackend = Object.keys(filasAgrupadas).map(nombreFila => {
        const asientosDeFila = filasAgrupadas[nombreFila];

        const minX = Math.min(...asientosDeFila.map(a => a.x));
        const maxX = Math.max(...asientosDeFila.map(a => a.x));
        const minY = Math.min(...asientosDeFila.map(a => a.y));
        const maxY = Math.max(...asientosDeFila.map(a => a.y));

        const centerX = (minX + maxX) / 2;
        const centerY = (minY + maxY) / 2;

        return {
          nombre: nombreFila,
          cantidad: asientosDeFila.length,
          coordY: Math.round(centerY),
          coordXIni: Math.round(centerX),
          separacion: 50,
          rotation: asientosDeFila[0].rotation || 0,
          asientosIndividuales: asientosDeFila.map((asiento, index) => ({
            index: index,
            coordX: Math.round(asiento.x),
            coordY: Math.round(asiento.y),
            rotation: asiento.rotation || 0
          }))
        };
      });

      const asientosIndividuales = elementosAjustados
        .filter(e => e.tipo === 'asiento' && !e.grupoFila)
        .map((a, index) => ({
          nombre: `A${a.numero || (index + 1)}`,
          coordX: Math.round(a.x),
          coordY: Math.round(a.y),
          rotation: a.rotation || 0,
          visible_cliente: true,
          numero: a.numero || (index + 1)
        }));

      const mesasValidadas = elementosAjustados.filter(e => e.tipo === 'mesa').map(m => ({
        capacidad: m.asientos,
        numeradas: m.numerada,
        coordX: Math.round(m.x),
        coordY: Math.round(m.y),
        rotation: m.rotation || 0,
        ancho: m.width,
        alto: m.height
      }));

      const response = await api.ubicacionesNew.updateLayoutZona(zona.id, {
        mesas: mesasValidadas,
        filas: filasParaBackend,
        asientos: asientosIndividuales
      });

      if (response.status === "success") {
        toast.success("Zona actualizada exitosamente.", {
          position: "top-right",
          autoClose: 3000,
        });
        onClose();
      }
    } catch (error) {
      console.error("Error guardando layout:", error);
      toast.error("Error guardando layout.", {
        position: "top-right",
        autoClose: 4000,
      });
    } finally {
      setLoading(false);
    }
  };

  const calcularCapacidadTotal = () => {
    const mesas = elementos.filter(e => e.tipo === 'mesa').reduce((sum, mesa) => sum + mesa.asientos, 0);
    const asientos = elementos.filter(e => e.tipo === 'asiento').length;
    return mesas + asientos;
  };


  const [escala, setEscala] = useState(1);
  const [posicion, setPosicion] = useState({ x: 0, y: 0 });

  const handleDragCanvas = (e) => {
    if (e.target === e.target.getStage() && !modoCreacion && !elementoSeleccionado) {
      setPosicion({ x: e.target.x(), y: e.target.y() });
    }
  };

  const zoomIn = () => {
    const newScale = Math.min(escala * 1.2, 3);
    setEscala(newScale);
  };

  const zoomOut = () => {
    const newScale = Math.max(escala * 0.8, 0.3);
    setEscala(newScale);
  };

  const resetZoom = () => {
    if (elementos.length > 0) {
      centrarCanvas();
    } else {
      setEscala(1);
      setPosicion({ x: 0, y: 0 });
    }
  };

  if (!zona) return null;
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '10px',
          boxShadow: '0 8px 32px rgba(44, 62, 80, 0.12)',
          bgcolor: '#ffff',
          p: 2
        }
      }}
    >
      <DialogTitle sx={{
        pb: 2,
        fontWeight: 700,
        fontSize: '18px',
        color: '#0b0060',
        borderBottom: '1px solid #E4E4E7',
        mb: 2
      }}>
        Diseñar Interior - {zona.ubicacion}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Configura la distribución de las mesas y asientos para esta zona.
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{
          display: 'flex',
          gap: 3,
          alignItems: 'flex-start'
        }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Paper elevation={0} sx={{
              width: 340,
              bgcolor: '#F9FAFB',
              borderRadius: '12px',
              p: 3,
              boxShadow: '0 1px 3px rgba(44,62,80,0.07)',
              border: '1px solid #E4E4E7',
              display: 'flex',
              flexDirection: 'column',
              gap: 1
            }}>
              <Typography variant="p" sx={{
                fontWeight: 700,
                fontSize: '18px',
                color: '#5656ff',
                mb: 1
              }}>
                {zona.tipo_ubicacion === 2 ? "Configuración de Mesas" : zona.tipo_ubicacion === 3 ? "Configuración de Filas" : "General"}
              </Typography>
              <Box sx={{ mb: 2, display: 'flex', gap: 1, flexDirection: 'column' }}>
                {zona.tipo_ubicacion === 2 && (
                  <>
                    <Typography variant="p" sx={{ mb: 0, color: "#0b0062" }}>Asientos por mesa</Typography>
                    <TextField
                      sx={{
                        fontFamily: "Bricolage Grotesque",
                        backgroundColor: "#ffff",
                        mb: 3,
                        '& .MuiInputBase-input-MuiOutlinedInput-input': {
                          fontFamily: "Bricolage Grotesque",
                        },
                        '& label.Mui-focused': {
                          color: '#5656FF',
                          fontFamily: "Bricolage Grotesque"
                        },
                        '& .MuiOutlinedInput-root': {
                          height: "40px",
                          '& fieldset': {
                            borderColor: '#B2BAC2',
                          },
                          '&:hover fieldset': {
                            borderColor: '#5656ff',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#5656ff',
                          },
                        }
                      }}
                      type="number"
                      value={configuracion.mesasConfig.asientosPorMesa}
                      onChange={(e) => {
                        const valor = e.target.value;
                        if (valor === '') {
                          setConfiguracion({
                            ...configuracion,
                            mesasConfig: {
                              ...configuracion.mesasConfig,
                              asientosPorMesa: ''
                            }
                          });
                        } else {
                          const numero = parseInt(valor);
                          if (!isNaN(numero) && numero > 0) {
                            setConfiguracion({
                              ...configuracion,
                              mesasConfig: {
                                ...configuracion.mesasConfig,
                                asientosPorMesa: numero
                              }
                            });
                          }
                        }
                      }}
                      onBlur={(e) => {
                        // al perder el foco si esta vacio uso un num predeterminado
                        if (e.target.value === '' || isNaN(parseInt(e.target.value))) {
                          setConfiguracion({
                            ...configuracion,
                            mesasConfig: {
                              ...configuracion.mesasConfig,
                              asientosPorMesa: 5
                            }
                          });
                        }
                      }}
                      size="small"
                      inputProps={{ min: 1, max: 20 }}
                    />
                    <FormControl sx={{
                      minWidth: 200,
                      '& .MuiOutlinedInput-root': {
                        fontFamily: "Bricolage Grotesque",
                        '& fieldset': {
                          borderColor: '#B2BAC2',
                        },
                        '&:hover fieldset': {
                          borderColor: '#5656ff',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#5656ff',
                        },
                        height: "40px",
                      },
                      '& .MuiFormHelperText-root': {
                        fontFamily: "Bricolage Grotesque",
                      }
                    }}>
                      <Typography variant="p" sx={{ mb: 1, display: 'block', color: "#0b0062" }}>
                        Tipo de mesa <Tooltip title="Mesa numerada: El cliente elige la silla exacta dentro de la mesa. Mesa no numerada: El cliente elige la mesa, no un asiento fijo." arrow placement="top">
                          <InfoOutlined
                            fontSize="small"
                            sx={{ ml: 1, color: '#5656ff', cursor: 'help' }}
                          />
                        </Tooltip>
                      </Typography>
                      <Select sx={{
                        backgroundColor: "#ffff",
                        '& .MuiSelect-select': {
                          padding: "8px 14px",
                          display: 'flex',
                          alignItems: 'center',
                          height: "24px",
                          width: "100%",
                        }
                      }}
                        value={configuracion.mesasConfig.numeradas}
                        onChange={(e) => setConfiguracion({
                          ...configuracion,
                          mesasConfig: {
                            ...configuracion.mesasConfig,
                            numeradas: e.target.value
                          }
                        })}
                      /* label="Tipo" */
                      >
                        <MenuItem value={true}>Numeradas</MenuItem>
                        <MenuItem value={false}>No numeradas</MenuItem>
                      </Select>
                    </FormControl>
                    <IconSelector
                      tipo="mesa"
                      iconoSeleccionado={configuracion.mesasConfig.icono}
                      onIconSelect={(icono) => setConfiguracion({
                        ...configuracion,
                        mesasConfig: { ...configuracion.mesasConfig, icono }
                      })}
                    />
                    <Button
                      variant={modoCreacion === 'mesa' ? "contained" : "outlined"}
                      onClick={() => setModoCreacion(modoCreacion === 'mesa' ? null : 'mesa')}
                      fullWidth
                      sx={{
                        borderRadius: '6px',
                        fontWeight: 500,
                        ...(modoCreacion === 'mesa' ? {
                          bgcolor: '#10b981',
                          color: 'white',
                          '&:hover': { bgcolor: '#059669' }
                        } : {
                          borderColor: '#6366f1',
                          color: '#6366f1',
                          '&:hover': {
                            borderColor: '#4f46e5',
                            bgcolor: 'rgba(99, 102, 241, 0.04)'
                          }
                        })
                      }}
                    >
                      {modoCreacion === 'mesa' ? "Cancelar creación" : "Agregar mesas (click en canvas)"}
                    </Button>
                  </>
                )}
                {zona.tipo_ubicacion === 3 && (
                  <>
                    <Typography variant="p" sx={{ ml: 1, display: 'block', color: "#0b0062" }}>
                      Asientos por fila
                    </Typography>
                    <TextField
                      type="number"
                      value={configuracion.filasConfig.asientosPorFila}
                      onChange={(e) => setConfiguracion({
                        ...configuracion,
                        filasConfig: {
                          ...configuracion.filasConfig,
                          asientosPorFila: parseInt(e.target.value)
                        }
                      })}
                      sx={{
                        fontFamily: "Bricolage Grotesque",
                        mb: 2,
                        backgroundColor: "#ffff",
                        '& .MuiInputBase-input-MuiOutlinedInput-input': {
                          fontFamily: "Bricolage Grotesque",
                        },
                        '& label.Mui-focused': {
                          color: '#5656FF',
                          fontFamily: "Bricolage Grotesque"
                        },
                        '& .MuiOutlinedInput-root': {
                          height: "40px",
                          '& fieldset': {
                            borderColor: '#B2BAC2',
                          },
                          '&:hover fieldset': {
                            borderColor: '#5656ff',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#5656ff',
                          },
                        }
                      }}
                      inputProps={{ min: 1, max: 50 }}
                    />
                    <Typography variant="p" sx={{ ml: 1, display: 'block', color: "#0b0062" }}>
                      Número de filas
                    </Typography>
                    <TextField
                      type="number"
                      value={configuracion.filasConfig.numeroFilas}
                      onChange={(e) => setConfiguracion({
                        ...configuracion,
                        filasConfig: {
                          ...configuracion.filasConfig,
                          numeroFilas: parseInt(e.target.value)
                        }
                      })}
                      sx={{
                        fontFamily: "Bricolage Grotesque",
                        backgroundColor: "#ffff",
                        mb: 2,
                        '& .MuiInputBase-input-MuiOutlinedInput-input': {
                          fontFamily: "Bricolage Grotesque",
                        },
                        '& label.Mui-focused': {
                          color: '#5656FF',
                          fontFamily: "Bricolage Grotesque"
                        },
                        '& .MuiOutlinedInput-root': {
                          height: "40px",
                          '& fieldset': {
                            borderColor: '#B2BAC2',
                          },
                          '&:hover fieldset': {
                            borderColor: '#5656ff',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#5656ff',
                          },
                        }
                      }}
                      inputProps={{ min: 1, max: 26 }}
                    />
                    <Typography variant="p" sx={{ ml: 1, display: 'block', color: "#0b0062" }}>
                      Espacio entre filas
                    </Typography>
                    <TextField
                      type="number"
                      value={configuracion.filasConfig.espaciado}
                      onChange={(e) => setConfiguracion({
                        ...configuracion,
                        filasConfig: {
                          ...configuracion.filasConfig,
                          espaciado: parseInt(e.target.value)
                        }
                      })}
                      sx={{
                        fontFamily: "Bricolage Grotesque",
                        backgroundColor: "#ffff",
                        mb: 2,
                        '& .MuiInputBase-input-MuiOutlinedInput-input': {
                          fontFamily: "Bricolage Grotesque",
                        },
                        '& label.Mui-focused': {
                          color: '#5656FF',
                          fontFamily: "Bricolage Grotesque"
                        },
                        '& .MuiOutlinedInput-root': {
                          height: "40px",
                          '& fieldset': {
                            borderColor: '#B2BAC2',
                          },
                          '&:hover fieldset': {
                            borderColor: '#5656ff',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#5656ff',
                          },
                        }
                      }}
                      inputProps={{ min: 20, max: 100 }}
                    />
                    <IconSelector
                      tipo="asiento"
                      iconoSeleccionado={configuracion.filasConfig.icono}
                      onIconSelect={(icono) => setConfiguracion({
                        ...configuracion,
                        filasConfig: { ...configuracion.filasConfig, icono }
                      })}
                    />
                    <Button
                      variant="contained"
                      onClick={crearFilas}
                      fullWidth
                      sx={{
                        bgcolor: '#6366f1',
                        borderRadius: '6px',
                        mb: 1,
                        fontWeight: 500,
                        '&:hover': { bgcolor: '#4f46e5' },
                        '&:disabled': {
                          bgcolor: '#e5e7eb',
                          color: '#9ca3af'
                        }
                      }}
                    >
                      Generar filas automáticamente
                    </Button>
                    <Button
                      sx={{
                        borderRadius: '6px',
                        fontWeight: 500,
                        ...(modoCreacion === 'asiento' ? {
                          bgcolor: '#10b981',
                          color: 'white',
                          '&:hover': { bgcolor: '#059669' }
                        } : {
                          borderColor: '#6366f1',
                          color: '#6366f1',
                          '&:hover': {
                            borderColor: '#4f46e5',
                            bgcolor: 'rgba(99, 102, 241, 0.04)'
                          }
                        })
                      }}
                      variant={modoCreacion === 'asiento' ? "contained" : "outlined"}
                      onClick={() => setModoCreacion(modoCreacion === 'asiento' ? null : 'asiento')}
                      fullWidth
                    >
                      {modoCreacion === 'asiento' ? "Cancelar" : "Agregar asientos individuales"}
                    </Button>
                  </>
                )}
                {/* {zona.tipo_ubicacion === 1 && (
                  <Alert severity="info">
                    Las zonas tipo General no requieren diseño interno.
                  </Alert>
                )} */}
              </Box>

            </Paper>

            {/* CARD RESUMEN */}
            {(tipoSeleccion === 'fila' || tipoSeleccion === 'elemento') && (
              <Box sx={{
                position: 'fixed',
                top: 50,
                right: 90,
                zIndex: 1500,
                display: 'flex',
                gap: 1,
                alignItems: 'center'
              }}>
                <IconButton
                  onClick={eliminarSeleccionado}
                  sx={{
                    bgcolor: '#ff4444',
                    color: 'white',
                    width: 45,
                    height: 45,
                    boxShadow: 3,
                    '&:hover': {
                      bgcolor: '#cc0000',
                      transform: 'scale(1.05)'
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  <DeleteOutline />
                </IconButton>

                <IconButton
                  onClick={() => rotarFilaSeleccionada(-15)}
                  sx={{
                    bgcolor: '#6366f1',
                    color: 'white',
                    width: 45,
                    height: 45,
                    boxShadow: 3,
                    '&:hover': { bgcolor: '#4f46e5', transform: 'scale(1.05)' },
                    transition: 'all 0.2s ease'
                  }}
                >
                  <RotateLeft />
                </IconButton>
                <IconButton
                  onClick={() => rotarFilaSeleccionada(15)}
                  sx={{
                    bgcolor: '#6366f1',
                    color: 'white',
                    width: 45,
                    height: 45,
                    boxShadow: 3,
                    '&:hover': { bgcolor: '#4f46e5', transform: 'scale(1.05)' },
                    transition: 'all 0.2s ease'
                  }}
                >
                  <RotateRight />
                </IconButton>

                {tipoSeleccion === 'fila' && (
                  <Chip
                    label={`Fila ${filaSeleccionada} seleccionada`}
                    size="small"
                    sx={{
                      bgcolor: '#4CAF50',
                      color: 'white',
                      fontWeight: 600,
                      boxShadow: 2
                    }}
                  />
                )}

                {tipoSeleccion === 'elemento' && elementoSeleccionado && (
                  <Chip
                    label={`${elementoSeleccionado.tipo === 'mesa' ? 'Mesa' : 'Asiento'} seleccionado`}
                    size="small"
                    sx={{
                      bgcolor: '#2196F3',
                      color: 'white',
                      fontWeight: 600,
                      boxShadow: 2
                    }}
                  />
                )}
              </Box>
            )}
            <Paper elevation={0} sx={{
              bgcolor: '#F9FAFB',
              borderRadius: '10px',
              p: 2,
              /* mt: 2, */
              border: '1px solid #E4E4E7'
            }}>
              <Typography variant="subtitle2" sx={{ color: '#5656ff', fontWeight: 600, mb: 1 }}>
                Resumen
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {zona.tipo_ubicacion === 2 ? "Mesas" : "Elementos"}: {elementos.length}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: calcularCapacidadTotal() > zona.limite_maximo ? "#dc2626" : "black",
                  fontWeight: 500
                }}
              >
                Capacidad ocupada: {calcularCapacidadTotal()} / {zona.limite_maximo}
                {calcularCapacidadTotal() > zona.limite_maximo && " ⚠️ Excede el límite"}
              </Typography>
              <Button
                variant="outlined"
                color="error"
                fullWidth
                sx={{
                  mt: 2,
                  borderRadius: '8px',
                  borderColor: '#f87171',
                  color: '#dc2626',
                  bgcolor: '#fff',
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: '#ef4444',
                    bgcolor: '#fee2e2'
                  }
                }}
                onClick={() => {
                  if (window.confirm('¿Eliminar todo el layout?')) {
                    setElementos([]);
                    setElementoSeleccionado(null);
                    setFilaSeleccionada(null);
                    setTipoSeleccion(null);
                    setTransformerVisible(false);

                    if (transformerRef.current) {
                      transformerRef.current.nodes([]);
                    }

                    setTimeout(() => {
                      setEscala(1);
                      setPosicion({ x: 0, y: 0 });
                    }, 50);
                  }
                }}
              >
                Limpiar todo
              </Button>
            </Paper>
          </Box>

          {/* CANVAS */}
          <Box sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center !important'
          }}>
            <Box sx={{ width: "100%", display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between" }}>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography variant="h6" sx={{
                  fontWeight: 700,
                  fontSize: '18px',
                  color: '#5656ff',
                  mb: 1,
                }}>
                  Vista previa de la zona
                </Typography>
                {/* {zona.tipo_ubicacion === 3 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0 }}>
                    <strong>Tip:</strong> Mantén Shift mientras arrastras un asiento para mover su fila.
                  </Typography>
                )} */}
              </Box>
              <Box sx={{
                display: 'flex',
                gap: 1,
                /* mt: 2, */
                alignItems: 'center',
                bgcolor: 'white',
                /* borderRadius: '8px', */
                p: 1,
                /* boxShadow: '0 2px 8px rgba(0,0,0,0.1)' */
              }}>
                <Button
                  size="small"
                  onClick={zoomOut}
                  sx={{ minWidth: '32px', height: '32px' }}
                >
                  −
                </Button>
                <Typography variant="body2" sx={{ mx: 1, minWidth: '60px', textAlign: 'center' }}>
                  {Math.round(escala * 100)}%
                </Typography>
                <Button
                  size="small"
                  onClick={zoomIn}
                  sx={{ minWidth: '32px', height: '32px' }}
                >
                  +
                </Button>
                <Button
                  size="small"
                  onClick={resetZoom}
                  sx={{ ml: 1, fontSize: '11px' }}
                >
                  Reset
                </Button>
              </Box>
            </Box>

            <Paper elevation={0} sx={{
              bgcolor: '#f8fafc',
              borderRadius: '12px',
              border: '2px dashed #E4E4E7',
              width: 700,
              height: "100%",
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxSizing: 'border-box'
            }}>
              <Stage
                width={CANVAS_WIDTH}
                height={CANVAS_HEIGHT}
                ref={stageRef}
                scaleX={escala}
                scaleY={escala}
                x={posicion.x}
                y={posicion.y}
                draggable={!modoCreacion && !transformerVisible}
                onDragEnd={handleDragCanvas}
                onClick={handleCanvasClick}
                onTap={handleCanvasClick}
                style={{
                  background: 'transparent',
                  display: 'block',
                  cursor: modoCreacion ? 'crosshair' : (transformerVisible ? 'default' : 'grab')
                }}
              >
                <Layer>
                  {elementos.map((elemento) => (
                    <ElementoCanvas key={elemento.id} elemento={elemento} />
                  ))}
                  <Transformer
                    ref={transformerRef}
                    rotateEnabled={false}
                    enabledAnchors={['top-left', 'top-right', 'bottom-left', 'bottom-right']}
                    onTransform={(e) => {
                      if (tipoSeleccion === 'elemento' && elementoSeleccionado) {
                        if (elementoSeleccionado.tipo === 'mesa') {
                          handleTransform(e);
                        }
                      }
                    }}
                    onTransformEnd={(e) => {
                      if (tipoSeleccion === 'fila') {
                        handleFilaTransformEnd(e);
                      } else if (tipoSeleccion === 'elemento' && elementoSeleccionado) {
                        if (elementoSeleccionado.tipo === 'mesa') {
                          handleMesaTransformEnd(e);
                        } else {
                          handleElementoTransformEnd(e);
                        }
                      }
                    }}
                    boundBoxFunc={(oldBox, newBox) => {
                      if (newBox.width < 20 || newBox.height < 20) return oldBox;
                      if (newBox.width > 800 || newBox.height > 800) return oldBox;
                      return newBox;
                    }}
                    anchorStroke="#4CAF50"
                    anchorFill="#4CAF50"
                    borderStroke="#4CAF50"
                    borderDash={[6, 2]}
                    anchorSize={8}
                    rotateAnchorOffset={30}
                    anchorCornerRadius={2}
                  />
                </Layer>
              </Stage>
            </Paper>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{
        px: 3,
        pb: 2,
        pt: 2,
        justifyContent: 'flex-end',
        bgcolor: '#f8f9fa',
        borderTop: '1px solid #E4E4E7'
      }}>
        <Button
          onClick={onClose}
          sx={{
            borderRadius: '8px',
            bgcolor: '#fff',
            color: '#1e293b',
            fontWeight: 600,
            px: 3,
            py: 1,
            mr: 2,
            border: '1px solid #E4E4E7',
            '&:hover': { bgcolor: '#f1f5f9' }
          }}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={guardarLayout}
          disabled={elementos.length === 0 || loading || calcularCapacidadTotal() > zona.limite_maximo}
          sx={{
            bgcolor: '#6366f1',
            borderRadius: '10px',
            fontWeight: 600,
            '&:hover': { bgcolor: '#4f46e5' },
            '&:disabled': {
              bgcolor: '#e5e7eb',
              color: '#9ca3af'
            },
            px: 3,
            py: 1,
            boxShadow: '0 1px 3px rgba(44,62,80,0.07)',
          }}
        >
          {loading ? 'Guardando...' :
            calcularCapacidadTotal() > zona.limite_maximo ?
              'Capacidad excedida' :
              `Guardar diseño (${elementos.length} elementos)`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditorLayout;